import * as React from "react"
import { cn } from "@/lib/utils"
import { DATE_FIELD_TYPES, type DateFieldType } from "@/lib/dateFieldMask"
import { DateFieldInput, FIELD_BOX } from "./date-field-input"

type InputProps = React.InputHTMLAttributes<HTMLInputElement>

/** Types whose native picker Chrome only opens from the icon, not the field. */
const PICKER_TYPES = ['month', 'week']

/** Firefox moves focus into the picker it opens, so the typed date never reaches the field. */
const PICKER_STEALS_TYPING = typeof navigator !== 'undefined' && /firefox/i.test(navigator.userAgent)

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, onClick, ...props }, ref) => {
    /* Date, time and date-time follow the app's language, not the browser's;
       see DateFieldInput. Same props, same ISO values. */
    if (type && DATE_FIELD_TYPES.includes(type)) {
      return <DateFieldInput ref={ref} type={type as DateFieldType} className={className} onClick={onClick} {...props} />
    }

    /**
     * Clicking a month or week field opens nothing in Chrome — only the small
     * icon on the right does, and it is a 16px target. `showPicker()` fixes it,
     * but it requires a user gesture and throws in a cross-origin iframe, hence
     * the try/catch: failing here means falling back to default behavior, not
     * breaking. Firefox is left alone: there the field takes typing and its own
     * calendar icon opens the picker.
     */
    const handleClick = (e: React.MouseEvent<HTMLInputElement>) => {
      onClick?.(e)
      if (!type || !PICKER_TYPES.includes(type) || PICKER_STEALS_TYPING) return
      if (e.defaultPrevented || e.currentTarget.disabled || e.currentTarget.readOnly) return
      try {
        e.currentTarget.showPicker?.()
      } catch {
        /* browser without support or permission — fall back to native */
      }
    }

    return (
      <input
        type={type}
        className={cn(FIELD_BOX, type && PICKER_TYPES.includes(type) && !PICKER_STEALS_TYPING && 'cursor-pointer', className)}
        ref={ref}
        onClick={handleClick}
        {...props}
      />
    )
  }
)
Input.displayName = "Input"

export { Input }
